import { useCertificates } from "@/hooks/useCertificates";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Award, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { openCertificatePrintWindow } from "@/lib/certificateTemplate";
import { toast } from "sonner";

export function CertificatesSection() {
  const { data: certificates, isLoading } = useCertificates();

  const handleDownload = (certificate: {
    curriculum_title: string;
    user_name: string;
    certificate_number: string;
    issued_at: string;
  }) => {
    const opened = openCertificatePrintWindow({
      userName: certificate.user_name,
      courseName: certificate.curriculum_title,
      certificateNumber: certificate.certificate_number,
      issuedAt: certificate.issued_at,
    });

    if (!opened) {
      toast.error("Your browser blocked the certificate window. Please allow pop-ups and try again.");
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            Certificates
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!certificates || certificates.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-primary" />
            Certificates
          </CardTitle>
          <CardDescription>Complete curricula to earn certificates</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-4">
            You haven't earned any certificates yet. Complete a curriculum to receive your first certificate!
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Award className="h-5 w-5 text-primary" />
          Certificates
        </CardTitle>
        <CardDescription>Your earned certificates of completion</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {certificates.map((cert) => (
          <div
            key={cert.id}
            className="flex items-center justify-between p-4 border rounded-lg bg-gradient-to-r from-primary/5 to-transparent"
          >
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Award className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h4 className="font-semibold">{cert.curriculum_title}</h4>
                <p className="text-sm text-muted-foreground">
                  Issued on {format(new Date(cert.issued_at), "MMMM d, yyyy")}
                </p>
                <p className="text-xs text-muted-foreground font-mono">
                  {cert.certificate_number}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleDownload(cert)}
            >
              <Download className="h-4 w-4 mr-2" />
              Download
            </Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
